// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from "@tanstack/react-query";
import { useHttpClient } from "@toggl/sync/http";
import {} from "../types";
import { MutationOptions } from "../utils/mutations";
import {
  deleteAttachmentMutationKey,
  postAttachmentsMutationKey,
  postQuestionAttachmentsMutationKey,
} from "./keys";
import {
  deleteAttachment,
  postAttachments,
  postQuestionAttachments,
} from "./requests";

export type DeleteAttachmentMutationVariables = {
  job_opening_slug: string;
  attachment_id: number;
};

export type DeleteAttachmentMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof deleteAttachment,
    DeleteAttachmentMutationVariables,
    TContext
  >;

/**
 * Deletes attachment matching the id in path
 */
export function useDeleteAttachmentMutation<TContext = unknown>(
  options: DeleteAttachmentMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, attachment_id }) =>
      deleteAttachment(httpClient, job_opening_slug, attachment_id),
    mutationKey: deleteAttachmentMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending deleteAttachment mutation. */
export function useDeleteAttachmentMutationVariables() {
  return useMutationState({
    filters: { mutationKey: deleteAttachmentMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as DeleteAttachmentMutationVariables,
  });
}

export type PostAttachmentsMutationVariables = {
  job_opening_slug: string;
  file_type: string;
  upload: File;
};

export type PostAttachmentsMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postAttachments,
    PostAttachmentsMutationVariables,
    TContext
  >;

/**
 * Uploads a multipart file for a candidate, the file type must be enabled in job opening
 */
export function usePostAttachmentsMutation<TContext = unknown>(
  options: PostAttachmentsMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, file_type, upload }) =>
      postAttachments(httpClient, job_opening_slug, file_type, upload),
    mutationKey: postAttachmentsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postAttachments mutation. */
export function usePostAttachmentsMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postAttachmentsMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PostAttachmentsMutationVariables,
  });
}

export type PostQuestionAttachmentsMutationVariables = {
  job_opening_slug: string;
  test_slug: string;
  question_id: string;
  upload: File;
};

export type PostQuestionAttachmentsMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postQuestionAttachments,
    PostQuestionAttachmentsMutationVariables,
    TContext
  >;

/**
 * Uploads a multipart file for a question, the question must exist and be homework type
 */
export function usePostQuestionAttachmentsMutation<TContext = unknown>(
  options: PostQuestionAttachmentsMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, test_slug, question_id, upload }) =>
      postQuestionAttachments(
        httpClient,
        job_opening_slug,
        test_slug,
        question_id,
        upload,
      ),
    mutationKey: postQuestionAttachmentsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postQuestionAttachments mutation. */
export function usePostQuestionAttachmentsMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: postQuestionAttachmentsMutationKey,
      status: "pending",
    },
    select: (mutation) =>
      mutation.state.variables as PostQuestionAttachmentsMutationVariables,
  });
}
